// src/components/mobile/srp/flightCard/mobileFlightCard.tsx
import HandBaggageOnlyIcon from '@/src/assets/svgs/HandBaggageOnlyIcon'
import { LongLayoverIcon, ShortLayoverIcon } from '@/src/assets/svgs/LayoverIcons'
import { PerAdultLabel } from '@/src/components/common/srp/perAdultLabel'
import { FlightCardProps } from '@/src/components/desktop/srp/flightCard'
import useFormatCurrencyForSrp from '@/src/hooks/currency/useFormatCurrencyForSrp'
import useFormatDate from '@/src/hooks/date/useFormatDate'
import { useFormatTime } from '@/src/hooks/srp/formatTime'
import useSearchResults from '@/src/hooks/srp/useSearchResults'
import { getAirlineIcons } from '@/src/utils/confirmation/airlineUtils'
import { getFormattedDate } from '@/src/utils/date'
import { replacePlaceHolderString } from '@/src/utils/generalUtils'
import CircleIcon from '@mui/icons-material/Circle'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { Box, IconButton, Typography } from '@mui/material'
import React, { useCallback } from 'react'

const MobileFlightCard: React.FC<FlightCardProps> = ({
    airlineName,
    airlineCodes,
    departureTime,
    arrivalTime,
    stops,
    totalDuration,
    price,
    seatsLeft,
    strikeOffPrice,
    departureAirport,
    arrivalAirport,
    isNbDeparture,
    isNbArrival,
    noBaggage,
    longLayover,
    shortLayover,
    coupon_detail,
    arrivalDate,
    departureDate,
    onBook,
    onViewDetails,
    tCommon,
    tFlightDetails,
    via
}) => {
    const { isPerPaxPricingEnabled } = useSearchResults()
    const formatTime = useFormatTime()
    const { formatCurrency } = useFormatCurrencyForSrp()
    const { formatDate } = useFormatDate()
    const duration = formatTime(totalDuration)
    const isNextDayCallOut = departureDate !== arrivalDate
    const handleViewDetails = useCallback(
        (e?: React.MouseEvent<HTMLButtonElement>) => {
            e?.stopPropagation()
            onViewDetails?.()
        },
        [onViewDetails]
    )
    const couponDetails = coupon_detail?.discountAmount
        ? {
              ...coupon_detail,
              discountedPrice: formatCurrency(coupon_detail.discountedPrice)
          }
        : {}
    return (
        <Box
            onClick={onBook}
            sx={{
                bgcolor: 'white',
                p: 2,
                cursor: 'pointer',
                position: 'relative',
                elevation: 0,
                display: 'flex',
                flexDirection: 'column'
            }}
        >
            {/* Row 1: */}
            <Box display="flex" alignItems="center" justifyContent="space-between" pb={0.5}>
                <Box display="flex" alignItems="center">
                    <Box display={'flex'} maxWidth={100} overflow={'hidden'} gap={0.5}>
                        {getAirlineIcons(airlineCodes, 24, 24)}
                    </Box>
                    <Typography
                        sx={{
                            mx: 1,
                            fontSize: 12,
                            textTransform: 'capitalize',
                            color: '#1F1F1F',
                            fontFamily: 'Plus Jakarta Sans',
                            fontWeight: 500
                        }}
                    >
                        {airlineName}
                    </Typography>
                </Box>
                <Box display="flex" alignItems="center" justifyContent="flex-end">
                    <IconButton onClick={handleViewDetails} sx={{ p: 0.5 }}>
                        <InfoOutlinedIcon sx={{ color: '#5c6672', fontSize: 20 }} />
                    </IconButton>
                </Box>
            </Box>

            {/* Row 2: */}
            <Box display="flex" alignItems="flex-start" justifyContent="space-between">
                <Box display="flex" alignItems="center">
                    <Typography
                        sx={{
                            fontSize: 16,
                            color: '#1F1F1F',
                            fontFamily: 'Plus Jakarta Sans',
                            fontWeight: 700
                        }}
                    >
                        {departureTime} – {arrivalTime}
                    </Typography>
                    {isNextDayCallOut && (
                        <Typography
                            sx={{
                                display: 'flex',
                                padding: '1px 4px',
                                justifyContent: 'center',
                                alignItems: 'center',
                                gap: '10px',
                                borderRadius: '4px',
                                background: '#EFF6FF',
                                color: '#184E91',
                                fontSize: '10px',
                                fontWeight: 500,
                                marginLeft: '4px'
                            }}
                        >
                            {formatDate(getFormattedDate(arrivalDate, 'MM/DD/YYYY'), 'D MMM')}
                        </Typography>
                    )}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {typeof seatsLeft === 'number' && seatsLeft > 0 && seatsLeft <= 5 && (
                        <Box
                            sx={{
                                display: 'flex',
                                width: '68px',
                                padding: '4px 8px',
                                alignItems: 'center',
                                gap: '4px',
                                borderRadius: '4px',
                                background: '#F8E8E8',
                                whiteSpace: 'nowrap',
                                color: '#B31F1F',
                                fontFamily: 'Plus Jakarta Sans',
                                fontSize: '10px',
                                fontStyle: 'normal',
                                fontWeight: 500,
                                lineHeight: 'normal'
                            }}
                        >
                            {seatsLeft}{' '}
                            {seatsLeft > 1
                                ? tFlightDetails('seatLeft', { s: 's' })
                                : tFlightDetails('seatLeft', { s: '' })}
                        </Box>
                    )}
                    <Typography
                        sx={{
                            whiteSpace: 'nowrap',
                            color: '#1F1F1F',
                            textAlign: 'right',
                            fontSize: '16px',
                            fontStyle: 'normal',
                            fontWeight: 700
                        }}
                    >
                        {formatCurrency(price)} <PerAdultLabel />
                    </Typography>
                </Box>
            </Box>

            {/* Row 3: */}
            <Box display="flex" alignItems="start" justifyContent="space-between">
                <Box display="flex" alignItems="center">
                    <Typography
                        sx={{
                            color: isNbDeparture ? '#184E91' : '#1F1F1F',
                            background: isNbDeparture ? '#EFF6FF' : 'transparent',
                            px: isNbDeparture ? 1 : 0,
                            borderRadius: isNbDeparture ? '4px' : 0,
                            display: isNbDeparture ? 'inline-block' : 'inline',
                            fontSize: '12px',
                            fontWeight: 500
                        }}
                    >
                        {departureAirport}
                    </Typography>
                    {via && (
                        <>
                            <Typography variant="body2" fontWeight={500} color="text.primary" mx={0.5}>
                                –
                            </Typography>
                            <Typography
                                variant="body2"
                                fontWeight={500}
                                sx={{
                                    color: '#7E7E7E',
                                    fontSize: '12px',
                                    fontWeight: 500
                                }}
                            >
                                {via}
                            </Typography>
                        </>
                    )}
                    <Typography variant="body2" fontWeight={500} color="text.primary" mx={0.5}>
                        –
                    </Typography>
                    <Typography
                        variant="body2"
                        fontWeight={500}
                        sx={{
                            color: isNbArrival ? '#184E91' : '#1F1F1F',
                            background: isNbArrival ? '#EFF6FF' : 'transparent',
                            px: isNbArrival ? 1 : 0,
                            borderRadius: isNbArrival ? '4px' : 0,
                            display: isNbArrival ? 'inline-block' : 'inline',
                            fontSize: '12px',
                            fontWeight: 500
                        }}
                    >
                        {arrivalAirport}
                    </Typography>
                    {longLayover && <LongLayoverIcon style={{ margin: '0px 5px' }} />}
                    {shortLayover && <ShortLayoverIcon style={{ margin: '0px 5px' }} />}
                </Box>
                {strikeOffPrice && (
                    <Box display="flex" alignItems="center">
                        <Typography
                            sx={{
                                color: '#1F1F1F',
                                textAlign: 'right',
                                fontSize: '12px',
                                fontStyle: 'normal',
                                fontWeight: 400,
                                textDecorationLine: 'line-through'
                            }}
                        >
                            {formatCurrency(strikeOffPrice)}
                        </Typography>
                    </Box>
                )}
            </Box>

            {/* Row 4: */}
            {coupon_detail?.discountAmount && coupon_detail?.discountAmount > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Typography
                        variant="caption"
                        fontSize={12}
                        sx={{
                            color: '#16a34a',
                            fontWeight: 500,
                            textAlign: 'right',
                            lineHeight: 1.3
                        }}
                    >
                        {replacePlaceHolderString(
                            isPerPaxPricingEnabled ? coupon_detail?.adultCouponMessage : coupon_detail?.message,
                            couponDetails
                        )}
                    </Typography>
                </Box>
            )}

            {/* Row 5 */}
            <Box display="flex" alignItems="center" gap={0.5}>
                <Typography
                    sx={{
                        fontFamily: 'Plus Jakarta Sans',
                        fontSize: '12px',
                        fontStyle: 'normal',
                        lineHeight: 'normal'
                    }}
                    color={stops === 0 ? ' #16a34a' : '#1F1F1F'}
                    fontWeight={stops === 0 ? 600 : 500}
                >
                    {stops === 0
                        ? tCommon('direct')
                        : `${stops} ${stops > 1 ? tCommon('stops', { s: 's' }) : tCommon('stops', { s: '' })}`}
                </Typography>
                <CircleIcon sx={{ fontSize: 4, color: 'grey.600' }} />
                <Typography
                    sx={{
                        color: '#1F1F1F',
                        fontFamily: 'Plus Jakarta Sans',
                        fontSize: '12px',
                        fontStyle: 'normal',
                        fontWeight: 600,
                        lineHeight: 'normal'
                    }}
                >
                    {duration}
                </Typography>
                {noBaggage && (
                    <>
                        <CircleIcon sx={{ fontSize: 4, color: 'grey.600' }} />
                        <HandBaggageOnlyIcon />
                    </>
                )}
            </Box>
        </Box>
    )
}

export { MobileFlightCard }
